/**
 * Job Status Derivation — schedule-based per Adrian's ruling (2026-09-02).
 *
 * A job's status is about SCHEDULING, not line-item coverage.
 *
 *   not_scheduled         (not completed) AND no work orders exist
 *   partially_scheduled   (not completed) AND >=1 WO exists AND not all are scheduled
 *                          — includes "work orders exist, none scheduled"
 *                          — includes "some scheduled, at least one not"
 *   scheduled             (not completed) AND >=1 WO exists AND EVERY one is scheduled
 *   completed             the user marked the job complete — STORED, not derived.
 *                         Wins regardless of work-order state.
 *
 * "Scheduled" means the work order carries a real date+time. `startAt` is the
 * field on the embedded work_orders[]. Portica measured at deploy b6a307d,
 * tenant 270: startAt populated on 550 of 550 WOs (full ISO timestamp
 * with time, not a midnight placeholder). `scheduledDate` is a duplicate;
 * `startTime`/`endTime` are populated on 1 of 550 — do NOT read them.
 *
 * IDI-246 (2026-10-31): the Complete control has shipped (complete_job /
 * reopen_job on cmd/jobs). A completed job stores status 'completed', which
 * wins; everything else is derived from the work orders.
 *
 * DO NOT reimplement any of these functions in a caller. If a caller wants
 * to know whether a job is on the calendar, `deriveJobStatus(job)` is the
 * truth. The three consumers (Jobs landing, Jobs filter chips, dashboard
 * tile+table) MUST all route through this file so `tile === table` holds
 * by construction — a discipline established by isJobBacklog after PR-40's
 * 54-vs-406 postmortem.
 *
 * IDI-143 §1 (2026-09-08, live-probed tenant 270, develop @ eff8eb9): jobs
 * carry NO scheduled/date field at the top level at all — 0 of 500 jobs had
 * scheduled_date, scheduledDate, or startAt directly on the job object.
 * `startAt`/`scheduledDate` only exist on the nested work_orders[] (1,059 of
 * 1,059 WOs on that tenant carry both, kept in sync by cmd/jobs/main.go).
 * This is WHY deriveJobStatus must read workOrders[].startAt instead of any
 * job-level field — a caller "optimizing" this to a direct job field read
 * will silently regress to reading a field that doesn't exist on the wire.
 */

/**
 * True when a work order is on the schedule (has a real startAt timestamp).
 * @param {Object} wo - a work order object
 * @returns {boolean}
 */
function isWorkOrderScheduled(wo) {
  // startAt is the canonical field per Portica's b6a307d measurement.
  // Guard against empty strings and non-string types — any truthy value
  // that would be present after a real schedule action.
  return Boolean(wo?.startAt);
}

/**
 * Derive a job's status from its work-order schedule state.
 *
 * @param {Object} job - Job object with workOrders array
 * @param {Array} job.workOrders - camelCase (Go-writes)
 * @param {Array} job.work_orders - snake_case (migration)
 * @returns {'not_scheduled' | 'partially_scheduled' | 'scheduled' | 'completed'}
 *   'completed' is the stored user act (IDI-246 complete_job).
 */
export function deriveJobStatus(job) {
  // IDI-393: a job's OWN status field takes precedence when it's terminal.
  // 5 jobs on tenant 1 carry status='completed' in the stored record, but
  // this function previously ignored job.status entirely and derived purely
  // from work orders — so a completed job with all-scheduled WOs returned
  // 'scheduled', and one with no WOs returned 'not_scheduled'. Both the
  // Completed chip and the Scheduled chip miscounted. Check the stored
  // status FIRST for terminal values; fall through to WO-based derivation
  // for the scheduling question (which only matters for non-terminal jobs).
  const storedStatus = (job?.status || '').toLowerCase();
  if (storedStatus === 'completed' || storedStatus === 'complete') {
    return 'completed';
  }
  // IDI-246 (D7, Adrian 09-02): a stored 'scheduled' answers nothing. Convert
  // and create still write it, but only the work orders say whether a job is
  // scheduled, so a job with none reads Not Scheduled and a reopened job
  // (stored 'active') reads its schedule status.

  const workOrders = job?.workOrders || job?.work_orders || [];

  // No WOs -> Not Scheduled.
  if (!workOrders.length) {
    return 'not_scheduled';
  }

  // Ruling: >=1 WO must exist AND EVERY WO must be scheduled.
  const allScheduled = workOrders.every(isWorkOrderScheduled);
  if (allScheduled) {
    return 'scheduled';
  }

  // At least one WO not on the calendar -> Partially Scheduled.
  return 'partially_scheduled';
}

/**
 * Map legacy status values to the canonical vocabulary.
 *
 * Only useful in contexts where the input is a STATUS STRING, not a job
 * object. If you have a job object, use `deriveJobStatus(job)` — the
 * stored `job.status` cannot answer the scheduling question (250 of 250
 * records are 'active' as of b6a307d).
 *
 * Retained for legacy call sites that receive raw status strings (e.g.
 * URL query params, filter chip labels).
 *
 * @param {string} status - Raw status from API or a query param
 * @returns {string} Canonical status
 */
export function normalizeJobStatus(status) {
  switch (status) {
    case 'in_progress':
    case 'inprogress':
      return 'partially_scheduled';
    case 'active':
      // Historical alias. Do NOT rely on this for a job object —
      // 250 of 250 jobs carry 'active' as their stored status.
      return 'partially_scheduled';
    case 'not_scheduled':
    case 'unscheduled':
      return 'not_scheduled';
    case 'complete':
    case 'completed':
      return 'completed';
    case 'scheduled':
      return 'scheduled';
    case 'partially_scheduled':
      return 'partially_scheduled';
    default:
      return 'not_scheduled';
  }
}

/**
 * isJobBacklog — "not scheduled" + "partially scheduled" predicate.
 *
 * The one predicate that BOTH the dashboard tile (`computeKPIs`
 * notScheduledJobs) AND `DashboardJobsTable`'s activeJobs use for their
 * counts. Named export so `tile === table` holds by construction, not by
 * three copies agreeing on a fixture the author picked.
 *
 * IDI-139 §2 (2026-09-03): now routes through `deriveJobStatus` instead
 * of `normalizeJobStatus(status)`. Under Adrian's ruling, the stored
 * `job.status` cannot answer the question — the answer lives in
 * `job.workOrders[].startAt`.
 *
 * If you pass a bare status STRING (rare — mostly filter-chip codepaths),
 * the function falls back to `normalizeJobStatus`. If you pass a JOB
 * OBJECT, the function calls `deriveJobStatus`. If you want to know
 * whether a job will be treated as backlog, `isJobBacklog(job)` is the
 * truth. Do NOT reimplement.
 *
 * @param {object|string} jobOrStatus — a job object OR a raw status string
 * @returns {boolean}
 */
export function isJobBacklog(jobOrStatus) {
  if (typeof jobOrStatus === 'string') {
    const n = normalizeJobStatus(jobOrStatus);
    return n === 'not_scheduled' || n === 'partially_scheduled';
  }
  const derived = deriveJobStatus(jobOrStatus);
  return derived === 'not_scheduled' || derived === 'partially_scheduled';
}
